// The top of the page: logo and name (left), tour button, tour list, language switch and full screen (right).
// Controls: brand, tour.button, tour.picker, language.switch, fullscreen.
import { html, safeUrl } from '../dom.js';
import { GENERAL } from '../content.js';
import { icon } from './icons.js';
import { popup } from './popup.js';

export function topParts(c) {
  const { api, T, tx, i18n } = c;
  const langs = () => c.v('lang.offered') || [];
  // WS-51: a tour is offered while it has a stop to play. The tour engine counts them (tours.js `playable`): a stop
  // at a place the preset hides (places.hidden, categories.hidden: the place is not on the map) or that is out of
  // season is skipped, with its `same` steps. A tour left with none cannot start, so the list does not name it.
  const offered = () => api.tours.list().filter((t) => t.stops > 0);
  const expanded = () => 'expanded' in (globalThis.document?.documentElement?.dataset || {}); // VIS-206: full window over a hotel's page (js/loader.js)
  let touring = false;
  const setTouring = (on) => {
    touring = on;
    const b = document.querySelector('#tour-btn');
    // VIS-128: the words are also its name (aria-label): on phones and beside an open card the button shows its icon alone
    if (b) { b.classList.toggle('on', on); b.querySelector('span').textContent = b.ariaLabel = T(on ? 'tour.stop' : 'tour.start'); }
  };

  const parts = {
    // brand.showName false = the logo alone (the name stays for screen readers). VIS-128: no logo set = no picture
    brand: {
      html() {
        const showName = c.v('brand.showName') !== false, name = tx(GENERAL.name), logo = c.v('brand.logo');
        return html`<header class="brand shell${showName ? '' : ' mark-only'}">
          <div class="core">
            ${logo ? html`<img src="${safeUrl(api.base + logo)}" alt="${showName ? '' : name}" ${showName ? html`width="40" height="40"` : html`height="40"`}>` : ''}
            ${showName ? html`<div><h1>${name}</h1><p>${tx(GENERAL.place)}</p></div>` : html`<h1 class="sr-only">${name}</h1>`}
          </div>
        </header>`;
      },
    },
    'tour.button': {
      can: () => offered().length > 0, // VIS-128: no tour to play, no button (as the tour list)
      html: () => html`<button class="pill-btn shell-btn" id="tour-btn">${icon('tour')}<span>${T('tour.start')}</span></button>`,
      wire(root) {
        root.querySelector('#tour-btn').onclick = () => (touring ? api.tours.stop() : api.tours.start());
        setTouring(touring);
      },
    },
    // the tours of this preset (tours.active): start one, or stop the one that runs
    'tour.picker': {
      can: () => offered().length > 0,
      html: () => html`<button class="round-btn" id="tours-btn" data-control="tour.picker" title="${T('tour.pick')}" aria-haspopup="dialog" aria-expanded="false">${icon('menu')}</button>`,
      wire(root) {
        const p = popup('tours-pop', root.querySelector('#tours-btn'), 'trees-pop menu-pop', T('tour.pick'));
        p.onOpen = () => {
          p.pop.innerHTML = html`<div class="tp-head"><span class="eyebrow">${T('tour.pick')}</span></div>
            ${offered().map((t) => html`<button class="pop-item" data-tour="${t.id}"><b>${tx(t.name)}</b><small>${T('tour.stops', { n: i18n.num(t.stops) })}</small></button>`)}
            ${touring ? html`<button class="pop-item" data-tour=""><b>${T('tour.stop')}</b></button>` : ''}`;
          p.pop.querySelectorAll('[data-tour]').forEach((b) => (b.onclick = () => { p.set(false); if (b.dataset.tour) api.tours.start(b.dataset.tour); else api.tours.stop(); }));
        };
      },
    },
    'language.switch': {
      can: () => langs().length > 1,
      html: () => html`<div class="langs" data-control="language.switch" role="radiogroup" aria-label="${T('lang.label')}">
        ${langs().map((l) => html`<button class="lang${l === i18n.lang ? ' on' : ''}" role="radio" aria-checked="${String(l === i18n.lang)}" data-lang="${l}" lang="${l}" title="${T(`set.lang.current.${l}`) || l}">${l}</button>`)}
      </div>`,
      wire: (root) => root.querySelectorAll('.lang').forEach((b) => (b.onclick = () => c.put({ 'lang.current': b.dataset.lang }))),
    },
    // VIS-128: only where the page may go full screen (not in a frame without the permission, not on an iPhone); a
    // markup stand without a page (the unit tests) draws it
    // VIS-206: a map opened full window from a website cover on a phone (js/loader.js, html[data-expanded]): the button
    // is its Close ('map-collapse'), in the same place, on every phone (also where full screen is not offered)
    // VIS-230: never in a phone's layout B (an iPhone cannot go full screen; there the loader's own Close stands in)
    fullscreen: {
      can: () => !c.phone && (!globalThis.document || document.fullscreenEnabled === true || expanded()),
      html: () => (expanded()
        ? html`<button class="round-btn" id="full-btn" data-close title="${T('fullscreen.close')}" aria-label="${T('fullscreen.close')}">${icon('close')}</button>`
        : html`<button class="round-btn" id="full-btn" title="${T('fullscreen')}">${icon('full')}</button>`),
      wire(root) {
        const b = root.querySelector('#full-btn');
        b.onclick = () => ('close' in b.dataset ? dispatchEvent(new Event('map-collapse'))
          : (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => {})); // errors:ignore: a refused request changes nothing
      },
    },
  };

  return { parts, setTouring };
}
